/**
 * La cartella Spam: i messaggi che il server ha scartato e quelli che il
 * filtro ha spostato, ognuno con il giudizio dell'AI e i suoi motivi.
 *
 * Qui si corregge il filtro: «Non è spam» rimette il messaggio in arrivo e
 * ricorda il mittente come sicuro; «È spam» lo conferma e può bloccarlo.
 * Dello spam si mostra solo il testo: niente HTML, niente link attivi.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Ban, CheckCircle2, Loader2, Paperclip, Settings2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api/client';
import { spamApi, type DettaglioSpam, type FiltroSpam, type ModoSpam, type VoceSpam } from '@/lib/api/endpoints';
import { ChipVerdetto, Motivi, RIQUADRI, presenta } from './Verdetto';

const NOME_MODO: Record<ModoSpam, string> = {
  off: 'spento',
  suggest: 'solo segnalazioni',
  prudent: 'prudente',
  auto: 'automatico',
};

interface Scelta { id: string; kind: VoceSpam['kind'] }

export function SpamView({ accountId }: { accountId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<FiltroSpam | null>(null);
  const [scelta, setScelta] = useState<Scelta | null>(null);

  const riepilogo = useQuery({
    queryKey: ['spam-summary', accountId],
    queryFn: () => spamApi.summary(accountId),
    refetchInterval: 30_000,
  });
  const r = riepilogo.data;
  // Si apre su «Da controllare» se c'è qualcosa, altrimenti su tutto.
  const filtroAttivo: FiltroSpam = filtro ?? (r && r.daControllare > 0 ? 'review' : 'all');

  const elenco = useQuery({
    queryKey: ['spam-items', accountId, filtroAttivo],
    queryFn: () => spamApi.items(accountId, filtroAttivo),
    refetchInterval: 30_000,
    enabled: !!r,
  });
  const voci = elenco.data?.items ?? [];

  function aggiorna() {
    void qc.invalidateQueries({ queryKey: ['spam-items', accountId] });
    void qc.invalidateQueries({ queryKey: ['spam-summary', accountId] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
  }

  const schede: { id: FiltroSpam; nome: string; n?: number }[] = [
    { id: 'review', nome: 'Da controllare', n: r?.daControllare },
    { id: 'all', nome: 'Tutti', n: r?.inSpam },
    { id: 'filtered', nome: 'Spostati dal filtro', n: r?.filtrate7g },
    { id: 'rescued', nome: 'Rimessi in arrivo', n: r?.rimesse7g },
  ];

  return (
    <div className="flex-1 flex min-w-0">
      <div className="w-[420px] border-r flex flex-col bg-background min-w-0">
        <div className="px-4 pt-4 pb-3 border-b space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Spam</h2>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              title="Impostazioni del filtro spam"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Filtro {r ? NOME_MODO[r.mode] : ''}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {schede.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setFiltro(s.id); setScelta(null); }}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs transition-colors',
                  filtroAttivo === s.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
                )}
              >
                {s.nome}{typeof s.n === 'number' && s.n > 0 ? ` · ${s.n}` : ''}
              </button>
            ))}
          </div>
          {r && r.mode === 'off' && (
            <p className="text-xs text-amber-700 dark:text-amber-400">Il filtro è spento: questa cartella mostra solo i messaggi senza giudizio.</p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {elenco.isLoading || riepilogo.isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : voci.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {filtroAttivo === 'review' ? 'Niente da controllare: il filtro non ha dubbi.' : 'Nessun messaggio in questa vista.'}
            </div>
          ) : (
            voci.map((v) => (
              <RigaSpam key={`${v.kind}-${v.id}`} voce={v} attiva={scelta?.id === v.id} onClick={() => setScelta({ id: v.id, kind: v.kind })} />
            ))
          )}
        </div>
      </div>

      {scelta ? (
        <SpamViewer
          key={scelta.id}
          scelta={scelta}
          onFatto={() => { setScelta(null); aggiorna(); }}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center bg-muted/10 text-muted-foreground">
          <div className="text-center max-w-sm px-6">
            <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-primary/70" />
            <div className="text-base font-medium text-foreground mb-1">Il filtro lavora per te</div>
            <p className="text-sm">
              Il server di posta mette qui ciò che sembra spam, e il filtro AI lo controlla: se una mail vera è finita qui,
              la trovi in «Da controllare» con il motivo. Seleziona un messaggio per vederlo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function RigaSpam({ voce, attiva, onClick }: { voce: VoceSpam; attiva: boolean; onClick: () => void }) {
  const nome = voce.from_address?.name || voce.from_address?.email || 'Sconosciuto';
  const quando = voce.received_at ? formatDistanceToNow(new Date(voce.received_at), { locale: it, addSuffix: false }) : '';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('w-full text-left px-4 py-3 border-b hover:bg-muted/50 transition-colors', attiva && 'bg-primary/5 border-l-2 border-l-primary')}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium truncate">{nome}</span>
        <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">{quando}</span>
      </div>
      <div className="text-sm truncate">{voce.subject || '(Nessun oggetto)'}</div>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <ChipVerdetto voce={voce} />
        {voce.has_attachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
      </div>
      {voce.reasons[0] && (voce.verdict === 'legit' || voce.verdict === 'uncertain') && (
        <div className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{voce.reasons[0]}</div>
      )}
    </button>
  );
}

function SpamViewer({ scelta, onFatto }: { scelta: Scelta; onFatto: () => void }) {
  const [fiducia, setFiducia] = useState<'email' | 'domain' | 'none'>('email');
  const [blocca, setBlocca] = useState(true);

  const dettaglio = useQuery({
    queryKey: ['spam-item', scelta.id],
    queryFn: () => spamApi.detail(scelta.id, scelta.kind),
  });
  const d: DettaglioSpam | undefined = dettaglio.data?.item;

  const nonSpam = useMutation({
    mutationFn: () => spamApi.notSpam(scelta.id, scelta.kind, fiducia),
    onSuccess: () => { toast.success('Rimessa in arrivo'); onFatto(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non sono riuscito a spostarla'),
  });
  const eSpam = useMutation({
    mutationFn: () => spamApi.confirmSpam(scelta.id, scelta.kind, blocca),
    onSuccess: () => { toast.success(blocca ? 'Confermato: mittente bloccato' : 'Confermato'); onFatto(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Operazione non riuscita'),
  });

  if (dettaglio.isLoading) {
    return <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!d) {
    return <div className="flex-1 flex items-center justify-center text-muted-foreground">Messaggio non trovato</div>;
  }

  const p = presenta(d);
  const Icona = p.icona;
  const dominio = (d.from_address?.email ?? '').split('@')[1];
  const rescued = d.action === 'rescued';
  // Il tasto in evidenza è quello che il giudizio suggerisce: per una truffa non è «Non è spam».
  const daSalvare = d.verdict === 'legit' || d.verdict === 'uncertain';
  const chi = d.decided_by === 'ai' ? 'dall’AI' : d.decided_by === 'rules' ? 'dalle regole del filtro' : 'da te';

  return (
    <div className="flex-1 flex flex-col bg-background overflow-hidden">
      <div className="border-b px-4 py-3 flex items-center gap-2 flex-wrap">
        {!rescued && (
          <>
            <Button size="sm" variant={daSalvare ? 'default' : 'outline'} onClick={() => nonSpam.mutate()} disabled={nonSpam.isPending || eSpam.isPending}>
              {nonSpam.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Non è spam
            </Button>
            <select
              value={fiducia}
              onChange={(e) => setFiducia(e.target.value as typeof fiducia)}
              className="h-8 rounded-md border bg-background px-2 text-xs"
              aria-label="Cosa ricordare del mittente"
            >
              <option value="email">Fidati di questo indirizzo</option>
              {dominio && <option value="domain">Fidati di tutto @{dominio}</option>}
              <option value="none">Non ricordare il mittente</option>
            </select>
            <span className="mx-1 h-5 w-px bg-border" />
            <Button size="sm" variant={daSalvare ? 'outline' : 'default'} onClick={() => eSpam.mutate()} disabled={nonSpam.isPending || eSpam.isPending}>
              <Ban className="h-4 w-4 mr-1.5" /> È spam
            </Button>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
              <input type="checkbox" checked={blocca} onChange={(e) => setBlocca(e.target.checked)} />
              blocca il mittente
            </Label>
          </>
        )}
        {rescued && <span className="text-sm text-muted-foreground">Questo messaggio è già stato rimesso in arrivo.</span>}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
          <div>
            <h1 className="text-2xl font-bold mb-3 break-words">{d.subject || '(Nessun oggetto)'}</h1>
            <div className="flex items-center justify-between text-sm gap-4">
              <div className="min-w-0">
                <div className="font-medium truncate">{d.from_address?.name || d.from_address?.email}</div>
                <div className="text-muted-foreground truncate">{d.from_address?.email}</div>
              </div>
              {d.received_at && (
                <div className="text-muted-foreground text-xs flex-shrink-0">{format(new Date(d.received_at), 'PPP - HH:mm', { locale: it })}</div>
              )}
            </div>
          </div>

          <div className={cn('rounded-lg border p-4 space-y-2', RIQUADRI[p.tono])}>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Icona className="h-4 w-4" />
              {p.titolo}
              {d.confidence !== null && d.decided_by !== 'user' && (
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  giudizio {chi} · sicurezza {d.confidence}%
                </span>
              )}
            </div>
            <Motivi motivi={d.reasons} />
          </div>

          <p className="text-xs text-muted-foreground">
            Per sicurezza qui vedi solo il testo: immagini, link e allegati restano disattivati finché non dici che non è spam.
          </p>

          {d.attachments.length > 0 && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Paperclip className="h-3 w-3" /> Allegati bloccati ({d.attachments.length})
              </div>
              {d.attachments.map((a, i) => (
                <div key={i} className="text-sm truncate">{a.filename}</div>
              ))}
            </div>
          )}

          <div className="border-t pt-6">
            {/* Solo testo semplice, senza link cliccabili. */}
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">{d.body_text || '(testo vuoto)'}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
